import { DonorType, PacDonationConfiguration } from "./state";

/**
 * Everything a non-individual donor has in common. Almost every donor-type branch on the donation
 * page is really asking this rather than naming a specific type -- the contact form shape, the
 * suppressed limits, the address handling. Branch on a specific type only where the rule genuinely
 * differs, which is the recipients a donor may give to and the terms they are shown; both of those
 * are decided server side.
 *
 * Defined against Individual rather than by listing the others, so a donor type added later is
 * treated as non-individual without having to remember to extend this.
 */
export const isNonIndividual = (donorType: DonorType): boolean => donorType != "Individual";

/** The donor types a page offers, in the order they are presented. */
export const allowedDonorTypes = (pacDonation?: PacDonationConfiguration): DonorType[] => {
    if (!pacDonation?.enabled) {
        return [];
    }

    const allowed: DonorType[] = [];

    if (pacDonation.allowFederalPac) {
        allowed.push("FederalPac");
    }
    if (pacDonation.allowCorporate) {
        allowed.push("Corporate");
    }
    if (pacDonation.allowStateLocalPac) {
        allowed.push("StateLocalPac");
    }

    return allowed;
};

/** Donor-facing wording. */
export const donorTypeLabels: Record<DonorType, string> = {
    Individual: "Individual Person",
    FederalPac: "Federal PAC",
    Corporate: "Corporation",
    StateLocalPac: "State/Local PAC"
};

/**
 * Labels for the organization fieldset. Corporate donors have no committee id, so the id field is
 * dropped rather than relabelled.
 *
 * `namePrompt` and `idPrompt` are the same fields phrased for a validation message -- "Enter the
 * PAC's name" rather than "Enter the PAC Name" -- so the wording matches the rest of the checkout
 * form, which reads "Enter a first name", "Enter a city".
 */
export const organizationFieldLabels = (donorType: DonorType): OrganizationFieldLabels => {
    switch (donorType) {
        case "Corporate":
            return {
                legend: "Corporate (including LLCs taxed as a Corporation) Information",
                name: "Corporation Legal Name",
                namePrompt: "the corporation's legal name",
                id: null,
                idPrompt: null
            };
        case "StateLocalPac":
            return {
                legend: "State/Local PAC Information",
                name: "PAC Name",
                namePrompt: "the PAC's name",
                id: "PAC ID",
                idPrompt: "the PAC's ID"
            };
        default:
            return {
                legend: "Federal PAC Information",
                name: "PAC Name",
                namePrompt: "the PAC's name",
                id: "FEC ID",
                idPrompt: "the PAC's FEC ID"
            };
    }
};

interface OrganizationFieldLabels {
    legend: string;
    name: string;
    namePrompt: string;
    /** Null for a donor type with no identifier, which drops the field rather than relabelling it. */
    id: string | null;
    idPrompt: string | null;
}
